import type { Control } from "react-hook-form";
import type { Person } from "../../../../utils/backend-types";
import { type applyHighlightingToObjectFormFieldData } from "../../../../utils/react-hook-form/applyHighlightingToObjectFormFieldData";
import { FormFieldController } from "../../../BuildingBlocks/Forms/Controllers/FormFieldController";
import { GeneralPartnerFields } from "../GeneralPartnerFields/GeneralPartnerFields";
import type { GeneralFieldsVisibility } from "../hooks/usePersonFormVisibility";
import { ParticipationFieldController } from "../ParticipationField/ParticipationFieldController";
import { type PERSON_FORM_FIELD_DATA } from "../PersonForm.constants";

interface GeneralFieldsProps {
  control: Control<Person>;
  fieldsThatCouldBeMissing?: Array<string>;
  formFieldData: ReturnType<
    typeof applyHighlightingToObjectFormFieldData<
      Person,
      typeof PERSON_FORM_FIELD_DATA
    >
  >["formFieldData"];
  visibility: GeneralFieldsVisibility;
}

function GeneralFields({
  control,
  fieldsThatCouldBeMissing,
  formFieldData,
  visibility
}: GeneralFieldsProps) {
  const {
    showAddressExtraField,
    showContactField,
    showGeneralPartnerFields,
    showIsErloesmonitoringActiveField,
    showIsParagraph_6EegActiveField,
    showIsRedispatchImportActiveField,
    showIsSolarContractorField,
    showMastrNummerMarktakteurField,
    showParticipationsInMieterstromModelField,
    showShouldRegulatoryDutiesBeCheckedField,
    showTaxFormsApplicantLegalStructureField
  } = visibility;

  return (
    <>
      <FormFieldController control={control} data={formFieldData.name} />

      {showIsSolarContractorField && (
        <FormFieldController
          control={control}
          data={formFieldData.isSolarContractor}
        />
      )}

      {showShouldRegulatoryDutiesBeCheckedField && (
        <FormFieldController
          control={control}
          data={formFieldData.shouldRegulatoryDutiesBeChecked}
        />
      )}

      {showParticipationsInMieterstromModelField && (
        <ParticipationFieldController
          control={control}
          isMissing={fieldsThatCouldBeMissing?.includes(
            "participationsInMieterstromModel"
          )}
          name="participationsInMieterstromModel"
        />
      )}

      {showIsParagraph_6EegActiveField && (
        <FormFieldController
          control={control}
          data={formFieldData.isParagraph_6EegActive}
        />
      )}

      {showIsErloesmonitoringActiveField && (
        <FormFieldController
          control={control}
          data={formFieldData.isErloesmonitoringActive}
        />
      )}

      {showIsRedispatchImportActiveField && (
        <FormFieldController
          control={control}
          data={formFieldData.isRedispatchImportActive}
        />
      )}

      <FormFieldController control={control} data={formFieldData.address} />

      {showAddressExtraField && (
        <FormFieldController
          control={control}
          data={formFieldData.addressExtra}
        />
      )}

      <FormFieldController control={control} data={formFieldData.zip} />

      <FormFieldController control={control} data={formFieldData.city} />

      {showContactField && (
        <FormFieldController control={control} data={formFieldData.contact} />
      )}

      {showTaxFormsApplicantLegalStructureField && (
        <FormFieldController
          control={control}
          data={formFieldData.taxFormsApplicantLegalStructure}
        />
      )}

      {showMastrNummerMarktakteurField && (
        <FormFieldController
          control={control}
          data={formFieldData.mastrNummerMarktakteur}
        />
      )}

      {showGeneralPartnerFields && (
        <GeneralPartnerFields control={control} formFieldData={formFieldData} />
      )}
    </>
  );
}

export { GeneralFields, type GeneralFieldsProps };
